import { createFrameCanvas, FrameCanvas, releaseFrameCanvas } from 'modules/match-video/domain/assembler/frame-canvas';
import { ReviewCanvasContext } from 'modules/match-video/domain/interfaces';

export const GRAIN_TILE_SIZE = 256;

export interface Grain {
  /** Lays the grain over the whole frame; `opacity` 0…1, blended as CSS `mix-blend-mode: overlay`. */
  draw(ctx: ReviewCanvasContext, opacity: number, width: number, height: number): void;
  dispose(): void;
}

/**
 * Fine grey noise tiled over a dark gradient. 8-bit H.264 turns a smooth dark gradient into visible steps; noise
 * at a few percent breaks the steps up (the mockup: `.a-grain`, 7 %, overlay). The tile is drawn once per scene and
 * stays still: noise that changes every frame costs the encoder bitrate for nothing.
 */
export function createGrain(random: () => number = Math.random, size = GRAIN_TILE_SIZE): Grain {
  const { canvas, ctx: tileCtx } = createFrameCanvas(size, size);
  const image = tileCtx.createImageData(size, size);
  for (let i = 0; i < image.data.length; i += 4) {
    const v = Math.floor(random() * 256);
    image.data[i] = v;
    image.data[i + 1] = v;
    image.data[i + 2] = v;
    image.data[i + 3] = 255;
  }
  tileCtx.putImageData(image, 0, 0);

  let tile: FrameCanvas | null = canvas;
  const patterns = new WeakMap<ReviewCanvasContext, CanvasPattern | null>();

  return {
    draw: (ctx, opacity, width, height) => {
      if (!tile || opacity <= 0) return;
      if (!patterns.has(ctx)) patterns.set(ctx, ctx.createPattern(tile, 'repeat'));
      const pattern = patterns.get(ctx);
      if (!pattern) return;
      ctx.save();
      ctx.globalCompositeOperation = 'overlay';
      ctx.globalAlpha = opacity;
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    },
    dispose: () => {
      if (tile) releaseFrameCanvas(tile);
      tile = null;
    },
  };
}
